
import React, { useState } from 'react';
import { ChatConversation, Language, UserRole } from '../types';
import { TRANSLATIONS, LANGUAGE_NAMES } from '../translations';
import { Search, Send, User, ArrowLeft, MoreVertical, Phone, Video, Languages } from 'lucide-react';

interface ChatViewProps {
  chats: ChatConversation[];
  currentUserRole: UserRole;
  language: Language;
}

// Mock translation function for demo purposes
// In a real app, this would call a translation API (Google/DeepL)
const getSimulatedTranslation = (text: string, targetLang: Language): string => {
    if (targetLang === 'en') return text; // Assume source is mostly English/Universal

    // Simple dictionary for demo interactions
    const dict: Record<string, Record<string, string>> = {
        'Hi! We loved your pitch.': {
            zh: '你好！我们很喜欢你的提案。',
            ja: 'こんにちは！あなたの提案は素晴らしいです。',
            ko: '안녕하세요! 당신의 제안이 마음에 듭니다.',
            fr: 'Salut! Nous avons aimé votre présentation.',
            es: '¡Hola! Nos encantó tu propuesta.'
        },
        'Thanks! I am excited to work on this.': {
            zh: '谢谢！我很期待这次合作。',
            ja: 'ありがとう！これに取り組むのが楽しみです。',
            fr: 'Merci! Je suis ravi de travailler là-dessus.'
        },
        'When can you start?': {
            zh: '你什么时候可以开始？',
            ja: 'いつ始められますか？'
        },
        'Sure, I can have the draft ready by tomorrow.': {
            zh: '没问题，我明天之前可以准备好初稿。',
            ja: 'もちろん、明日までにドラフトを用意できます。'
        }
    };

    if (dict[text] && dict[text][targetLang as string]) {
        return dict[text][targetLang as string];
    }

    // Fallback for demo: just prefix
    return `[${LANGUAGE_NAMES[targetLang]}] ${text}`;
};

export const ChatView: React.FC<ChatViewProps> = ({ chats, currentUserRole, language }) => {
  const t = TRANSLATIONS[language];
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  
  // Mobile responsiveness state: If chat is selected, we show chat window full screen on mobile
  const activeChat = chats.find(c => c.id === selectedChatId);

  const handleSendMessage = () => {
    if (!messageInput.trim() || !activeChat) return;
    setMessageInput('');
  };

  return (
    <div className="h-[calc(100vh-8rem)] bg-slate-800 border border-slate-700 rounded-xl overflow-hidden flex shadow-2xl animate-in fade-in duration-500">
      
      {/* Sidebar / Contact List */}
      <div className={`w-full md:w-80 border-r border-slate-700 flex flex-col bg-slate-900/50 ${selectedChatId ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-slate-700">
          <h2 className="text-lg font-bold text-white mb-4">{t.chats}</h2>
          <div className="relative">
             <Search className="absolute left-3 top-2.5 text-slate-500 w-4 h-4" />
             <input 
               type="text" 
               placeholder={t.searchContacts}
               className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
             />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
           {chats.map(chat => (
             <div 
               key={chat.id}
               onClick={() => setSelectedChatId(chat.id)}
               className={`p-4 border-b border-slate-800/50 cursor-pointer hover:bg-slate-800 transition-colors ${selectedChatId === chat.id ? 'bg-slate-800 border-l-4 border-l-indigo-500' : ''}`}
             >
               <div className="flex items-center gap-3">
                 <div className="relative">
                   <img src={chat.partnerAvatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                   <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-900"></div>
                 </div>
                 <div className="flex-1 min-w-0">
                   <div className="flex justify-between items-start mb-0.5">
                      <h3 className="font-bold text-white text-sm truncate">{chat.partnerName}</h3>
                      <span className="text-[10px] text-slate-500">{chat.lastMessageTime}</span>
                   </div>
                   <p className={`text-xs truncate ${chat.unreadCount > 0 ? 'text-white font-medium' : 'text-slate-400'}`}>
                     {chat.lastMessage}
                   </p>
                 </div>
                 {chat.unreadCount > 0 && (
                   <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                     {chat.unreadCount}
                   </div>
                 )}
               </div>
             </div>
           ))}
        </div>
      </div>

      {/* Chat Window */}
      <div className={`flex-1 flex flex-col bg-slate-800 ${!selectedChatId ? 'hidden md:flex' : 'flex'}`}>
         {activeChat ? (
           <>
             {/* Chat Header */}
             <div className="h-16 border-b border-slate-700 px-6 flex justify-between items-center bg-slate-900/30">
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setSelectedChatId(null)}
                    className="md:hidden text-slate-400 hover:text-white"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <img src={activeChat.partnerAvatar} alt="" className="w-10 h-10 rounded-full" />
                  <div>
                    <h3 className="font-bold text-white text-sm">{activeChat.partnerName}</h3>
                    <div className="flex items-center gap-1.5">
                       <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                       <span className="text-xs text-slate-400">{t.online}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 text-slate-400">
                   <button className="hover:text-white"><Phone size={20} /></button>
                   <button className="hover:text-white"><Video size={20} /></button>
                   <button className="hover:text-white"><MoreVertical size={20} /></button>
                </div>
             </div>

             {/* Messages Area */}
             <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-900/20">
                <div className="text-center text-xs text-slate-500 my-4">Today</div>
                {activeChat.messages.map(msg => {
                  const isTranslated = language !== 'en'; // Assume source is 'en' for mock data
                  const translatedText = getSimulatedTranslation(msg.text, language);

                  return (
                    <div key={msg.id} className={`flex ${msg.isMe ? 'justify-end' : 'justify-start'}`}>
                       <div className={`max-w-[70%] rounded-2xl px-4 py-3 text-sm ${
                          msg.isMe 
                            ? 'bg-indigo-600 text-white rounded-tr-sm' 
                            : 'bg-slate-700 text-white rounded-tl-sm'
                       }`}>
                          {/* Main Text (Translated or Original) */}
                          <p>{isTranslated ? translatedText : msg.text}</p>
                          
                          {/* Auto-Translation Indicator & Original Text */}
                          {isTranslated && (
                              <div className="mt-2 pt-2 border-t border-white/20">
                                  <div className="flex items-center gap-1 text-[10px] text-indigo-200 mb-1">
                                     <Languages size={10} />
                                     <span>{t.translatedMessage}</span>
                                  </div>
                                  <p className="text-xs text-slate-300 italic">{msg.text}</p>
                              </div>
                          )}

                          <p className={`text-[10px] mt-1 text-right ${msg.isMe ? 'text-indigo-200' : 'text-slate-400'}`}>
                             {msg.timestamp}
                          </p>
                       </div>
                    </div>
                  );
                })}
             </div>

             {/* Input Area */}
             <div className="p-4 bg-slate-900 border-t border-slate-700">
               <div className="flex gap-2">
                 <input 
                    type="text" 
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder={t.typeMessage}
                    className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-indigo-500"
                 />
                 <button 
                   onClick={handleSendMessage}
                   className="bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-xl transition-colors"
                 >
                    <Send size={18} />
                 </button>
               </div>
             </div>
           </>
         ) : (
           <div className="flex-1 flex flex-col items-center justify-center text-slate-500 p-8 text-center">
              <div className="w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center mb-4">
                 <User size={32} />
              </div>
              <p>{t.noChatSelected}</p>
           </div>
         )}
      </div>
    </div>
  );
};
